import { useCallback, type Dispatch, type SetStateAction } from 'react'

import type { UserRole } from '../utils/permissions'
import { projectsAPI } from '../services/api'
import {
  addMemberToProjectList,
  assertAuthenticatedProjectUser,
  assertMutableProjectMember,
  assertNewProjectMember,
  assertProjectMemberAdmin,
  assertValidMemberEmail,
  findProjectOrThrow,
  removeMemberFromProjectList,
  updateMemberRoleInProjectList,
} from './projectMemberHelpers'
import type { Project, ProjectMember } from './ProjectContextTypes'

interface ProjectMemberActionsArgs {
  projects: Project[]
  setProjects: Dispatch<SetStateAction<Project[]>>
  currentProject: Project | null
  setCurrentProject: Dispatch<SetStateAction<Project | null>>
  currentUserEmail: string | null
  getCurrentUserRole: (projectId: string) => UserRole | null
}

export function useProjectMemberActions({
  projects,
  setProjects,
  currentProject,
  setCurrentProject,
  currentUserEmail,
  getCurrentUserRole,
}: ProjectMemberActionsArgs) {
  const addProjectMember = useCallback(
    async (projectId: string, email: string, role: UserRole): Promise<boolean> => {
      try {
        assertValidMemberEmail(email)
        const project = findProjectOrThrow(projects, projectId)
        assertNewProjectMember(project, email)
        assertProjectMemberAdmin(getCurrentUserRole(projectId))
        assertAuthenticatedProjectUser(
          currentUserEmail,
          '인증된 사용자 이메일이 없어 멤버를 추가할 수 없습니다.'
        )

        await projectsAPI.addMember(projectId, {
          email,
          role,
          addedBy: currentUserEmail,
        })

        const newMember: ProjectMember = {
          email,
          role,
          addedAt: new Date().toISOString().split('T')[0],
          addedBy: currentUserEmail,
        }

        setProjects(prev => addMemberToProjectList(prev, projectId, newMember))
        if (currentProject?.id === projectId) {
          setCurrentProject(prev =>
            prev ? addMemberToProjectList([prev], projectId, newMember)[0] : null
          )
        }

        return true
      } catch (error: unknown) {
        console.error('멤버 추가 실패:', error)
        return false
      }
    },
    [currentProject?.id, currentUserEmail, getCurrentUserRole, projects, setCurrentProject, setProjects],
  )

  const updateMemberRole = useCallback(
    async (projectId: string, email: string, role: UserRole): Promise<boolean> => {
      try {
        const project = findProjectOrThrow(projects, projectId)
        assertMutableProjectMember(project, email)
        assertAuthenticatedProjectUser(
          currentUserEmail,
          '인증된 사용자 이메일이 없어 멤버 역할을 변경할 수 없습니다.'
        )
        assertProjectMemberAdmin(getCurrentUserRole(projectId))

        await projectsAPI.updateMemberRole(projectId, email, role)
        setProjects(prev => updateMemberRoleInProjectList(prev, projectId, email, role))

        if (currentProject?.id === projectId) {
          setCurrentProject(prev =>
            prev ? updateMemberRoleInProjectList([prev], projectId, email, role)[0] : null
          )
        }

        return true
      } catch (error: unknown) {
        console.error('역할 변경 실패:', error)
        return false
      }
    },
    [currentProject?.id, currentUserEmail, getCurrentUserRole, projects, setCurrentProject, setProjects],
  )

  const removeMember = useCallback(
    async (projectId: string, email: string): Promise<boolean> => {
      try {
        const project = findProjectOrThrow(projects, projectId)
        assertMutableProjectMember(project, email)
        assertAuthenticatedProjectUser(
          currentUserEmail,
          '인증된 사용자 이메일이 없어 멤버를 제거할 수 없습니다.'
        )
        assertProjectMemberAdmin(getCurrentUserRole(projectId))

        await projectsAPI.removeMember(projectId, email)
        setProjects(prev => removeMemberFromProjectList(prev, projectId, email))

        if (currentProject?.id === projectId) {
          setCurrentProject(prev =>
            prev ? removeMemberFromProjectList([prev], projectId, email)[0] : null
          )
        }

        return true
      } catch (error: unknown) {
        console.error('멤버 제거 실패:', error)
        return false
      }
    },
    [currentProject?.id, currentUserEmail, getCurrentUserRole, projects, setCurrentProject, setProjects],
  )

  return {
    addProjectMember,
    removeMember,
    updateMemberRole,
  }
}
